import React from 'react'
import {
	Select,
	makeStyles,
	OutlinedInput,
	InputLabel,
	FormControl,
	Typography,
	SelectProps
} from '@material-ui/core'
import StyledIconWithTooltip from './StyledIconWithTooltip'
import { gray, purple } from '../../utils/colors'
import { FONT_WEIGHTS } from '../../utils'

const useStyles = ({ hasLabel = true }) => {
	const styles = {
		container: {
			display: 'flex',
			flex: 1,
			flexDirection: 'column',
			gap: '.3rem',
			'& .MuiFormControl-root': {
				flex: 1,
				'& .MuiFormLabel-root': {
					zIndex: 'unset'
				},
				'& .MuiInputLabel-shrink': {
					transform: 'translate(12px, -5px) scale(0.75)',
					zIndex: 1
				}
			},
			'& .MuiInputBase-root': {
				margin: 0,
				flex: 1,
				minWidth: 'unset',
				borderBottomRightRadius: 0,
				borderBottomLeftRadius: 0,
				fontSize: '1rem',
				fontFamily: 'Nunito',
				'& .MuiSelect-icon': {
					right: '10px'
				},
				'& .MuiSelect-icon.Mui-disabled': {
					color: gray[500],
					fill: gray[500]
				},
				'& .MuiSelect-select.Mui-disabled': {
					backgroundColor: gray[200],
					borderTopColor: gray[500],
					borderLeftColor: gray[500],
					borderRightColor: gray[500],
					'& > div': {
						backgroundColor: gray[200]
					}
				}
			},
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: gray[500],
				borderBottomColor: gray[600],
				borderBottomWidth: '1px'
			},
			'& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
				borderBottomWidth: 1,
				borderColor: gray[500]
			},
			'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
				borderColor: gray[500],
				borderWidth: 1,
				borderBottomColor: purple[500],
				borderBottomWidth: '2px'
			},
			'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
				borderWidth: 1,
				borderColor: gray[500],
				borderBottomColor: gray[600],
				borderBottomWidth: '2px'
			}
		},
		header: {
			fontFamily: 'Nunito',
			fontSize: '0.75rem',
			color: gray[900],
			textTransform: 'uppercase',
			fontWeight: FONT_WEIGHTS.ExtraBold,
			letterSpacing: '0.0625rem'
		},
		headerContainer: {
			display: 'flex',
			alignItems: 'center',
			gap: '.5rem'
		}
	}
	if (!hasLabel) {
		styles.container['& legend'] = {
			display: 'none'
		}
	}
	return makeStyles(() => styles)
}

/**
 * A StyledSelect with optional header element
 * @param {SelectProps & {header?: string | undefined}} props
 * @returns
 */

const StyledSelect = (props) => {
	const classes = useStyles({ hasLabel: props?.label > '' })()
	return (
		<div className={classes.container}>
			{props.header ? (
				props.tooltip && !props.disabled ? (
					<span className={classes.headerContainer}>
						<Typography variant="overline">{props.header}</Typography>
						<StyledIconWithTooltip
							placement={props.tooltip.placement || 'right-start'}
							tooltipChildren={props.tooltip.tooltipChildren || undefined}
							tooltipText={props.tooltip.tooltipText || ''}
							faIcon={props.tooltip.faIcon || undefined}
							skiptownvariant={props.tooltip.skiptownvariant || undefined}
						/>
					</span>
				) : (
					<Typography variant="overline">{props.header}</Typography>
				)
			) : null}
			<FormControl>
				{props.label > '' ? (
					<InputLabel>{`${props.label}${props.required ? ' *' : ''}`}</InputLabel>
				) : null}
				<Select input={<OutlinedInput label={props.label || null} />} {...props}>
					{props.children}
				</Select>
			</FormControl>
		</div>
	)
}

export default StyledSelect
